import React, { useEffect, useMemo, useRef, useState } from 'react'
import Dropzone from 'react-dropzone'
import Papa from 'papaparse'
import * as Icons from 'react-feather'
import { toast } from 'sonner'
import InputEnter from '../generic/InputEnter'
import { getSelectedClients } from '../data/interactions'
import { getModel, updateModel } from '../data/Model'
import { getApiPath, requestAPI } from '../data/api'
import NoClientsSelected from './NoClientsSelected'
import CircularProgress from '@material-ui/core/CircularProgress'

const ROW_HEIGHT = 44 // px, must match .wl-row height in App.css for windowing
const LIST_HEIGHT = 560 // px visible viewport
const MAX_RESULT_ROWS = 100 // cap rendered rows per import-result group

export default function Whitelist({ client_ids }) {
  const [search, setSearch] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [addingSingle, setAddingSingle] = useState(false)
  const [upload, setUpload] = useState(null) // { fileName, status, total, summary }
  const loadedClientRef = useRef(null)

  const selected = getSelectedClients()
  const clientId = selected && selected[0] ? selected[0].client_id : null

  const model = getModel()
  const whitelist = model.whitelist
  const data = whitelist.data || []
  const isLoading = whitelist.status.handle === 'loading'
  const isLoaded = whitelist.status.handle === 'loaded'

  const filtered = useMemo(() => {
    const sorted = data.slice().sort((a, b) => a.url.localeCompare(b.url))
    const q = search.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter((u) => u.url.toLowerCase().includes(q))
  }, [data, search])

  useEffect(() => {
    if (!clientId) return
    if (loadedClientRef.current === clientId) return
    loadedClientRef.current = clientId
    setSearch('')
    setUrlInput('')
    setUpload(null)
    getWhitelist(clientId)
  }, [clientId])

  if (!client_ids || client_ids.length === 0) return <NoClientsSelected />

  const onAddSingle = async (value) => {
    const url = (value || '').trim()
    if (!url || addingSingle) return
    setAddingSingle(true)
    const results = await addWhitelistUrls(clientId, [url])
    setAddingSingle(false)
    const result = Array.isArray(results) ? results[0] : null
    if (!result) {
      toast.error('Something went wrong while adding the domain')
      return
    }
    if (result.status === 'added') {
      toast.success(`Added ${result.uri}`)
      setUrlInput('')
      await getWhitelist(clientId)
    } else if (result.status === 'duplicate') {
      toast.warning(`${result.uri || url} is already whitelisted`)
    } else {
      toast.error(`"${url}" is not a valid domain`)
    }
  }

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      toast.error('Unsupported file. Please upload a .csv or text file.')
      return
    }
    const file = acceptedFiles[0]
    if (!file) return
    setUpload({ fileName: file.name, status: 'parsing', total: 0, summary: null })
    Papa.parse(file, {
      download: false,
      skipEmptyLines: true,
      complete: (csv) => onParsed(file, csv),
    })
  }

  const onParsed = async (file, csv) => {
    const urls = csv.data
      .map((row) => (row[0] || '').trim())
      .filter(Boolean)
    if (urls.length === 0) {
      toast.error('No domains found in the file')
      setUpload(null)
      return
    }
    setUpload({ fileName: file.name, status: 'uploading', total: urls.length, summary: null })
    const results = await addWhitelistUrls(clientId, urls)
    await getWhitelist(clientId)
    const summary = summarize(results)
    setUpload({ fileName: file.name, status: 'done', total: urls.length, summary })
    toast.success(`${summary.added.length} domain(s) added`, {
      description: `${summary.duplicate.length} duplicate · ${summary.invalid.length} invalid`,
    })
  }

  const onDelete = async (url) => {
    await deleteWhitelistUrl(clientId, url)
    toast.success(`Removed ${url}`)
    await getWhitelist(clientId)
  }

  const uploading = upload && (upload.status === 'parsing' || upload.status === 'uploading')

  return (
    <div id="whitelist" className="container">
      <h1 style={{ marginTop: '30px', marginBottom: '20px' }}>Whitelist</h1>

      <div className="row">
        {/* ---- Left: add / import / export ---- */}
        <div className="col-md-5">
          <div className="wl-panel">
            <label className="wl-label">Add a single domain</label>
            <InputEnter
              value={urlInput}
              disabled={addingSingle}
              onChange={(e) => setUrlInput(e.target.value)}
              onEnter={(value) => onAddSingle(value)}
              placeholder="example.com"
            />
            <p className="wl-hint">
              Existing, duplicate and invalid domains will not be added.
            </p>
          </div>

          <div className="wl-panel">
            <label className="wl-label">Import from CSV</label>
            <p className="wl-hint">
              One domain per row, first column only. Example: <code>example.com</code>,{' '}
              <code>app.example.com</code>
            </p>

            {uploading ? (
              <div className="wl-dropzone wl-dropzone-busy">
                <CircularProgress size={26} />
                <span className="wl-dropzone-text">
                  {upload.status === 'parsing'
                    ? `Reading ${upload.fileName}…`
                    : `Adding ${upload.total} domain(s) from ${upload.fileName}…`}
                </span>
              </div>
            ) : (
              <Dropzone
                onDrop={onDrop}
                multiple={false}
                accept="text/*, application/vnd.ms-excel,"
                className="wl-dropzone"
                activeClassName="wl-dropzone-active"
                rejectClassName="wl-dropzone-reject"
              >
                <Icons.UploadCloud size={28} />
                <span className="wl-dropzone-text">
                  Drag a CSV here, or click to choose a file
                </span>
              </Dropzone>
            )}

            {upload && upload.status === 'done' && (
              <UploadResults
                summary={upload.summary}
                onDismiss={() => setUpload(null)}
              />
            )}
          </div>

          {clientId && (
            <a
              download
              className="wl-export"
              href={getApiPath(
                `/whitelist?clientId=${clientId}&type=text/csv&auth_token=${model.auth_token}`,
              )}
            >
              <Icons.Download size={15} /> Export CSV
            </a>
          )}
        </div>

        {/* ---- Right: searchable list ---- */}
        <div className="col-md-7">
          <div className="wl-list-header">
            <div className="wl-search">
              <Icons.Search size={16} className="wl-search-icon" />
              <input
                className="wl-search-input"
                type="text"
                value={search}
                placeholder="Search domains…"
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="wl-search-clear"
                  onClick={() => setSearch('')}
                  title="Clear"
                >
                  <Icons.X size={15} />
                </button>
              )}
            </div>
            <span className="wl-count">
              {search
                ? `${filtered.length} of ${data.length}`
                : `${data.length} domain${data.length === 1 ? '' : 's'}`}
            </span>
          </div>

          {isLoading ? (
            <div className="wl-empty">
              <CircularProgress size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="wl-empty">
              {data.length === 0 && isLoaded
                ? 'No domains whitelisted yet.'
                : 'No domains match your search.'}
            </div>
          ) : (
            <VirtualList
              items={filtered}
              rowHeight={ROW_HEIGHT}
              height={LIST_HEIGHT}
              renderRow={(u) => (
                <WhitelistRow key={u.id} url={u.url} onDelete={onDelete} />
              )}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Lightweight, dependency-free windowing: only the rows in (and just around)
// the viewport are mounted, so a 20k+ list renders ~30 nodes instead of 20k.
// Requires a fixed row height (ROW_HEIGHT / .wl-row).
function VirtualList({ items, rowHeight, height, overscan = 10, renderRow }) {
  const [scrollTop, setScrollTop] = useState(0)
  const total = items.length
  const viewportRows = Math.ceil(height / rowHeight)
  // Clamp start so a shrunk list (e.g. after filtering while scrolled deep)
  // still renders its tail before the next scroll event corrects scrollTop.
  const maxStart = Math.max(0, total - viewportRows - overscan)
  const start = Math.min(
    maxStart,
    Math.max(0, Math.floor(scrollTop / rowHeight) - overscan),
  )
  const end = Math.min(total, start + viewportRows + overscan * 2)
  const slice = items.slice(start, end)

  return (
    <div
      className="wl-list"
      style={{ height, overflowY: 'auto' }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: total * rowHeight }}>
        <div style={{ transform: `translateY(${start * rowHeight}px)` }}>
          {slice.map((item) => renderRow(item))}
        </div>
      </div>
    </div>
  )
}

function WhitelistRow({ url, onDelete }) {
  const [confirming, setConfirming] = useState(false)
  const [removing, setRemoving] = useState(false)

  const remove = async () => {
    setRemoving(true)
    await onDelete(url)
    // component unmounts on success; no need to reset state
  }

  return (
    <div className="wl-row">
      <span className="wl-row-url" title={url}>
        {url}
      </span>
      {confirming ? (
        <span className="wl-row-actions">
          <button
            className="wl-icon-btn wl-confirm-yes"
            onClick={remove}
            disabled={removing}
            title="Confirm remove"
          >
            {removing ? <CircularProgress size={14} /> : <Icons.Check size={15} />}
          </button>
          <button
            className="wl-icon-btn wl-confirm-no"
            onClick={() => setConfirming(false)}
            disabled={removing}
            title="Cancel"
          >
            <Icons.X size={15} />
          </button>
        </span>
      ) : (
        <button
          className="wl-icon-btn wl-row-delete"
          onClick={() => setConfirming(true)}
          title="Remove"
        >
          <Icons.Trash2 size={15} />
        </button>
      )}
    </div>
  )
}

function UploadResults({ summary, onDismiss }) {
  const groups = [
    { key: 'added', label: 'Added', items: summary.added, tone: 'ok' },
    { key: 'duplicate', label: 'Already whitelisted', items: summary.duplicate, tone: 'warn' },
    { key: 'invalid', label: 'Invalid', items: summary.invalid, tone: 'err' },
  ]
  return (
    <div className="wl-results">
      <div className="wl-results-head">
        <span>Import complete</span>
        <button className="wl-icon-btn" onClick={onDismiss} title="Dismiss">
          <Icons.X size={15} />
        </button>
      </div>
      {groups.map((g) => (
        <ResultGroup key={g.key} label={g.label} tone={g.tone} items={g.items} />
      ))}
    </div>
  )
}

function ResultGroup({ label, tone, items }) {
  const [open, setOpen] = useState(false)
  const count = items.length
  return (
    <div className={`wl-result-group wl-tone-${tone}`}>
      <button
        className="wl-result-toggle"
        onClick={() => count > 0 && setOpen(!open)}
        disabled={count === 0}
      >
        <span className="wl-chip">{count}</span>
        <span className="wl-result-label">{label}</span>
        {count > 0 &&
          (open ? <Icons.ChevronDown size={15} /> : <Icons.ChevronRight size={15} />)}
      </button>
      {open && (
        <ul className="wl-result-list">
          {items.slice(0, MAX_RESULT_ROWS).map((r, i) => (
            <li key={`${r.input}-${i}`}>
              {r.uri && r.uri !== r.input ? (
                <span>
                  {r.uri} <span className="wl-result-from">({r.input})</span>
                </span>
              ) : (
                r.input
              )}
            </li>
          ))}
          {count > MAX_RESULT_ROWS && (
            <li className="wl-result-more">
              + {count - MAX_RESULT_ROWS} more
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

function summarize(results) {
  const summary = { added: [], duplicate: [], invalid: [] }
  ;(Array.isArray(results) ? results : []).forEach((r) => {
    if (r.status === 'added') summary.added.push(r)
    else if (r.status === 'duplicate') summary.duplicate.push(r)
    else summary.invalid.push(r)
  })
  return summary
}

async function getWhitelist(client_id) {
  await updateModel((model) => {
    model.whitelist.status.handle = 'loading'
    model.whitelist.data = []
    return model
  })
  return requestAPI(`/whitelist?clientId=${client_id}`, 'GET').then((urls) => {
    updateModel((model) => {
      if (Array.isArray(urls)) model.whitelist.data = urls
      model.whitelist.status.handle = 'loaded'
      model.whitelist.status.timestamp = new Date()
      return model
    })
  })
}

async function addWhitelistUrls(clientId, urls) {
  return requestAPI('/whitelist', 'POST', { clientId, urls })
}

async function deleteWhitelistUrl(client_id, url) {
  return requestAPI('/whitelist', 'DELETE', { clientId: client_id, url })
}
